import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiClipboard, FiFileText, FiUsers } from 'react-icons/fi';
import { useAuth } from './AuthContext';
import { getApiBase } from '../utils/runtime';
import { useCareCoordinator } from '../care/CareCoordinatorContext';
import { useLanguage } from '../i18n/LanguageContext';
import { FeedbackPanel, ObservationPanel, RecommendationPanel, TimelinePanel } from './care/CarePanels';
import './ProfessionalDashboard.css';

const API_BASE = getApiBase();

function ProfessionalDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const {
    managedSubjects,
    activeSubjectId,
    recommendations,
    observations,
    auditLog,
    addObservation,
    addFeedback,
    recordRecommendationAction
  } = useCareCoordinator();
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const isFrench = language === 'fr';

  useEffect(() => {
    if (!user?.user_id) return;

    Promise.all([
      fetch(`${API_BASE}/appointments/professional/${user.user_id}`).then((res) => res.json()).catch(() => ({ appointments: [] })),
      fetch(`${API_BASE}/professionals/${user.user_id}/services`).then((res) => res.json()).catch(() => []),
      fetch(`${API_BASE}/professionals/${user.user_id}/specialities`).then((res) => res.json()).catch(() => [])
    ]).then(([appointmentsResponse, serviceResponse, specialityResponse]) => {
      setAppointments(appointmentsResponse.appointments || []);
      setServices(Array.isArray(serviceResponse) ? serviceResponse : []);
      setSpecialities(Array.isArray(specialityResponse) ? specialityResponse : []);
    });
  }, [user]);

  const handleDeleteSpeciality = async (specialityId) => {
    if (!user?.user_id) return;
    const response = await fetch(`${API_BASE}/professionals/${user.user_id}/specialities/${specialityId}`, {
      method: 'DELETE'
    });
    if (response.ok) {
      setSpecialities((current) => current.filter((item) => item.speciality_id !== specialityId));
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!user?.user_id) return;
    const response = await fetch(`${API_BASE}/professionals/${user.user_id}/services/${serviceId}`, {
      method: 'DELETE'
    });
    if (response.ok) {
      setServices((current) => current.filter((item) => item.service_id !== serviceId));
    }
  };

  const nextAppointment = useMemo(() => {
    return appointments.find((appointment) => new Date(appointment.start_utc || appointment.start) > new Date()) || null;
  }, [appointments]);

  const handleRecommendationAction = (recommendation, action) => {
    recordRecommendationAction(recommendation, action);
    if (action === 'defer') return;
    if (recommendation.actionType === 'provider-observation') return navigate('/professional/clients');
    if (recommendation.actionType === 'booking') return navigate('/booking', { state: { conciergePrefill: recommendation.bookingPrefill || null } });
  };

  return (
    <div className="dashboard-container">
      <div className="welcome-header">
        <h1>{isFrench ? 'Espace de coordination prestataire' : 'Provider coordination workspace'}</h1>
        <p>{isFrench ? 'Session restauree pour' : 'Persistent session restored for'} {user?.first_name || user?.given_name || user?.email}.</p>
        <p>{isFrench ? 'Utilisez des observations structurees, des jalons et un contexte autorise pour guider les prochains suivis.' : 'Use structured observations, milestones, and consent-aware context to support follow-up recommendations.'}</p>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card blue" onClick={() => navigate('/professional/calendrier')}>
          <span className="dashboard-icon"><FiCalendar /></span>
          <h3>{isFrench ? 'Rendez-vous a venir' : 'Upcoming appointments'}</h3>
          <p>{nextAppointment ? (isFrench ? `${nextAppointment.service_name} avec ${nextAppointment.patient_name}` : `${nextAppointment.service_name} with ${nextAppointment.patient_name}`) : (isFrench ? 'Aucun rendez-vous a venir pour le moment.' : 'No upcoming appointment in view.')}</p>
          <button className="btn-small" onClick={() => navigate('/professional/calendrier')}>{isFrench ? 'Ouvrir le calendrier' : 'Open calendar'}</button>
        </div>

        <div className="dashboard-card purple" onClick={() => navigate('/professional/clients')}>
          <span className="dashboard-icon"><FiUsers /></span>
          <h3>{isFrench ? 'Patients suivis' : 'Client relationships'}</h3>
          <p>{appointments.length} {isFrench ? 'contexte(s) patient lies aux rendez-vous disponibles.' : 'appointment-linked patient contexts available.'}</p>
          <button className="btn-small" onClick={() => navigate('/professional/clients')}>{isFrench ? 'Voir les patients' : 'Open clients'}</button>
        </div>

        <div className="dashboard-card green">
          <span className="dashboard-icon"><FiClipboard /></span>
          <h3>{isFrench ? 'Observations structurees' : 'Structured observations'}</h3>
          <p>{observations.length} {isFrench ? 'note(s) utiles pour les suivis enregistree(s).' : 'note(s) captured for follow-up orchestration.'}</p>
        </div>

        <div className="dashboard-card orange">
          <span className="dashboard-icon"><FiFileText /></span>
          <h3>{isFrench ? 'Configuration clinique' : 'Clinical setup'}</h3>
          <p>{specialities.length} {isFrench ? 'specialites' : 'specialties'} • {services.length} {isFrench ? 'services' : 'services'}</p>
          <div className="list">
            {specialities.slice(0, 3).map((item) => <span key={item.speciality_id || item.name}>{item.name}</span>)}
          </div>
        </div>
      </div>

      <div className="dashboard-grid dashboard-grid--legacy">
        <div className="dashboard-card blue" onClick={() => navigate('/professional/profil')}>
          <h3>{isFrench ? 'Profil professionnel' : 'Professional profile'}</h3>
          <p>{isFrench ? 'Gardez vos renseignements professionnels a jour.' : 'Keep your professional information up to date.'}</p>
        </div>
        <div className="dashboard-card purple" onClick={() => navigate('/professional/ajouter-specialite')}>
          <h3>{isFrench ? 'Gerer les specialites' : 'Manage specialties'}</h3>
          <div className="list">
            {specialities.map((item) => (
              <span key={item.speciality_id || item.name}>
                {item.name}
                {item.speciality_id ? <button type="button" onClick={(event) => { event.stopPropagation(); handleDeleteSpeciality(item.speciality_id); }}>x</button> : null}
              </span>
            ))}
          </div>
          <button className="btn-small" onClick={() => navigate('/professional/ajouter-specialite')}>{isFrench ? 'Ajouter une specialite' : 'Add specialty'}</button>
        </div>
        <div className="dashboard-card green" onClick={() => navigate('/professional/ajouter-service')}>
          <h3>{isFrench ? 'Gerer les services' : 'Manage services'}</h3>
          <div className="list">
            {services.map((item) => (
              <span key={item.service_id || item.name}>
                {item.name}
                {item.service_id ? <button type="button" onClick={(event) => { event.stopPropagation(); handleDeleteService(item.service_id); }}>x</button> : null}
              </span>
            ))}
          </div>
          <button className="btn-small" onClick={() => navigate('/professional/ajouter-service')}>{isFrench ? 'Ajouter un service' : 'Add service'}</button>
        </div>
        <div className="dashboard-card turquoise" onClick={() => navigate('/professional/add-availability')}>
          <h3>{isFrench ? 'Disponibilites' : 'Availability'}</h3>
          <p>{isFrench ? 'Ajoutez ou mettez a jour vos disponibilites sans modifier le parcours de planification existant.' : 'Add or update availability without changing the existing scheduling flow.'}</p>
          <button className="btn-small" onClick={() => navigate('/professional/add-availability')}>{isFrench ? 'Gerer les disponibilites' : 'Manage availability'}</button>
        </div>
      </div>

      <div className="dashboard-layout">
        <div className="dashboard-main">
          <RecommendationPanel title={isFrench ? 'Suggestions de suivi pour le prestataire' : 'Provider follow-up suggestions'} recommendations={recommendations} onAction={handleRecommendationAction} />
          <ObservationPanel subjects={managedSubjects} activeSubjectId={activeSubjectId} onSubmit={addObservation} />
          <FeedbackPanel onSubmit={(payload) => addFeedback({ ...payload, linkedId: nextAppointment?.id || null })} />
        </div>
        <div className="dashboard-side">
          <TimelinePanel observations={observations} auditLog={auditLog} />
          <div className="dashboard-card turquoise">
            <h3>{isFrench ? 'Contexte autorise seulement' : 'Authorized context only'}</h3>
            <p>{isFrench ? 'Les details patient affiches ici proviennent uniquement des rendez-vous lies, des consentements et des observations enregistrees dans cet espace.' : 'Patient detail is surfaced only from linked appointments, consent, and structured care events added in this workspace.'}</p>
            <button className="btn-small" onClick={() => navigate('/professional/clients')}>{isFrench ? 'Voir la liste patient' : 'Review patient list'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfessionalDashboard;
